import { Button, FormField, Input } from '@wix/design-system';
import React, { useContext, useRef, useState } from 'react';

import { generateId } from './../../ricos-content';
import { EditorContext, PluginsEventsContext, RicosContext } from './../../ricos-context';
import type { AudioData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { LogBiFn } from './../../_shared/bi-context-provider/bi-context-provider';
import { containsUrl } from './../../_shared/link/contains-url';
import { ReactPlayer } from './../../wix-react-player';
import { MEDIA_POPOVERS_BUTTONS_NAMES_BI } from './../../wix-rich-content-common';

import { EMBED_TAB_SUBMIT_BUTTON, EMBED_TAB_URL_INPUT } from '../data-hooks';
import classes from '../statics/styles/audio-embed-scss';
import type { AudioPluginEditorConfig } from '../types';
import { AUDIO_TYPE } from '../types';
import { isSoundcloudUrl } from './utils';

type AudioEmbedProps = {
  nodeId?: string;
  componentData: AudioData;
  closeModal: () => void;
  fetchData: AudioPluginEditorConfig['fetchData'];
  logBi: LogBiFn;
};

export const AudioEmbed: React.FC<AudioEmbedProps> = ({
  componentData,
  fetchData,
  closeModal,
  nodeId,
  logBi,
}) => {
  const { t } = useContext(RicosContext);
  const { getPublicCommands } = useContext(EditorContext);
  const pluginsEvents = useContext(PluginsEventsContext);

  const [url, setUrl] = useState(componentData?.audio?.src?.url || '');
  const [submittedInvalidUrl, setSubmittedInvalidUrl] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onEmbedClick = async () => {
    pluginsEvents.publishPluginPopoverClick?.({
      pluginId: AUDIO_TYPE,
      buttonName: MEDIA_POPOVERS_BUTTONS_NAMES_BI.embed,
    });

    logBi({ dataHook: EMBED_TAB_SUBMIT_BUTTON });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { audio, name, coverImage, authorName, disableDownload, ...rest } = componentData;

    // The order of functions matters, do not try to refactor it!!
    if (isSoundcloudUrl(url)) {
      await fetchAndEmbedAudio(url, rest);
    } else if (ReactPlayer.canPlay(url)) {
      updateRicosDocument({ audio: { src: { url } } });
      closeModal();
    } else {
      await fetchAndEmbedAudio(url, rest);
    }
  };

  const fetchAndEmbedAudio = async (url: string, data: Partial<AudioData>) => {
    try {
      const { html } = (await fetchData?.(url)) || {};
      if (html) {
        updateRicosDocument({ ...data, audio: { src: { url } }, html });
        closeModal();
      } else {
        setSubmittedInvalidUrl(true);
        inputRef.current?.focus();
      }
    } catch {
      setSubmittedInvalidUrl(true);
      inputRef.current?.focus();
    }
  };

  const updateRicosDocument = (audio: AudioData) =>
    queueMicrotask(() =>
      nodeId
        ? getPublicCommands().setBlock(nodeId, AUDIO_TYPE, audio)
        : getPublicCommands().insertBlockWithBlankLines(AUDIO_TYPE, { ...audio, id: generateId() })
    );

  return (
    <>
      <FormField
        label={t('AudioModal_Embed_Title')}
        maxLines={2}
        status={submittedInvalidUrl ? 'error' : undefined}
        statusMessage={
          submittedInvalidUrl ? t('SoundCloudUploadModal_Input_InvalidUrl') : undefined
        }
      >
        <Input
          inputRef={ref => {
            inputRef.current = ref;
          }}
          className={classes.input}
          type="url"
          autocomplete="off"
          dataHook={EMBED_TAB_URL_INPUT}
          value={url}
          textOverflow="ellipsis"
          onChange={e => {
            setSubmittedInvalidUrl(false);

            setUrl(e.target.value);

            logBi({ dataHook: EMBED_TAB_URL_INPUT, value: e.target.value });
          }}
          placeholder={t('EmbedURL_Placeholder')}
          onBlur={() => {
            if (url && !containsUrl(url)) {
              setSubmittedInvalidUrl(true);
            }
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onEmbedClick();
            }
          }}
        />
      </FormField>
      <Button dataHook={EMBED_TAB_SUBMIT_BUTTON} onClick={onEmbedClick} size="small">
        {t('AudioModal_Embed_ButtonText')}
      </Button>
    </>
  );
};
